import React, { useState, useEffect, useRef } from "react";
import PlayIcon from "../../ui/icon/carousel/PlayIcon";
import "./CustomVideoPlayer.css";
import { useCarouselContext } from "../../../context/CarouselContext";
import LoaderCss from "../../ui/loader/LoaderCss";

const CustomVideoPlayer = ({
  src: videoSource,
  preview,
  ramkaLoaded,
  index: itemIndex,
}) => {
  const {
    setShowPreview,
    showPreview,
    setShowText,
    currentSlide,
    videoLoaded,
    setVideoLoaded,
    currentCategory,
    setVideoPreview,
    showPlayIcon,
    setShowPlayIcon,
    isPlaying,
    setIsPlaying,
  } = useCarouselContext();

  const [videoLoader, setVideoLoader] = useState(false);
  const [previewLoaded, setPreviewLoaded] = useState(false);
  const [wasPlayIconClicked, setWasPlayIconClicked] = useState(false);
  const [metadataLoad, setMetadataLoad] = useState();
  const videoRef = useRef(null);
  const previewRef = useRef(null);
  const abortControllerRef = useRef(null);
  const isActive = currentSlide === itemIndex;

  const cancelPreviousRequest = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
  };

  const startVideo = () => {
    setShowText(false);
    setShowPlayIcon(false);
    videoRef.current?.play().catch((error) => {
      console.error("Error playing video:", error);
    });
    setIsPlaying(true);
  };

  const toggleVideo = () => {
    setTimeout(() => {
      if (previewLoaded) {
        setShowPlayIcon(true);
      }
    }, 25);
    videoRef.current?.pause();
    setIsPlaying(false);
    setShowPreview(false);
  };

  useEffect(() => {
    if (isPlaying) {
      videoRef.current.currentTime = 0;
      toggleVideo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSlide]);

  useEffect(() => {
    const videoElement = videoRef.current;
    const handleVolumeChange = () => {
      videoElement.muted = true;
    };

    videoElement.addEventListener("volumechange", handleVolumeChange);

    return () => {
      videoElement.removeEventListener("volumechange", handleVolumeChange);
    };
  }, [videoRef]);

  const stopVideo = () => {
    setShowText(true);
    videoRef.current?.pause();
    setIsPlaying(false);
  };

  const markVideoReady = () => {
    setMetadataLoad(true);
    setVideoLoaded(true);
    setVideoLoader(false);
  };

  useEffect(() => {
    if (videoRef.current) {
      stopVideo();
      setWasPlayIconClicked(false);
    }
    cancelPreviousRequest();
    setVideoLoader(false);
    setTimeout(() => {
      setShowPreview(true);
    }, 25);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCategory, currentSlide]);

  const handleVideoClick = () => {
    if (!showPreview) {
      stopVideo();
      setShowPlayIcon(true);
      setShowPreview(false);
    }
  };

  const handleIconClick = () => {
    if (!wasPlayIconClicked) {
      setVideoLoader(true);
      setWasPlayIconClicked(true);
    }

    if (videoLoaded || metadataLoad || videoRef.current?.readyState >= 2) {
      markVideoReady();
      startVideo();
      setShowPreview(false);
    } else {
      videoRef.current?.load();
    }
  };

  useEffect(() => {
    if (wasPlayIconClicked && videoLoaded) {
      videoRef.current.currentTime = 0;
      startVideo();
      setShowPreview(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wasPlayIconClicked, videoLoaded]);

  const previewVideoLoaded = () => {
    setPreviewLoaded(true);

    if (isActive) {
      setVideoPreview(true);
    }
  };

  useEffect(() => {
    if (!isActive) {
      return;
    }

    const previewElement = previewRef.current;
    if (previewElement?.complete && previewElement.naturalWidth > 0) {
      setPreviewLoaded(true);
      setVideoPreview(true);
    }
  }, [isActive, preview, setVideoPreview]);
 
  useEffect(() => {
    if (isActive && previewLoaded) {
      setVideoPreview(true);
      const timeout = setTimeout(() => {
        setShowPlayIcon(true);
      }, 500);
      return () => clearTimeout(timeout);
    }

    if (isActive) {
      setShowPlayIcon(false);
    }
  }, [isActive, previewLoaded, setShowPlayIcon, setVideoPreview]);

  return (
    <div
      className={`${
        ramkaLoaded ? "visible" : "invisible"
      } menu-frame-media`}
    >
      <div
        className={`h-full ${previewLoaded && "ombre"} ${
          !showPreview && "opacity-0"
        } `}
      >
        <img
          ref={previewRef}
          className={`w-full object-cover`}
          src={preview}
          alt="Video thumbnail"
          onLoad={previewVideoLoaded}
          onError={previewVideoLoaded}
        />
      </div>

      {isActive && !previewLoaded && <LoaderCss />}
      {videoLoader && <LoaderCss />}

      <video
        ref={videoRef}
        className={`w-full h-full absolute top-0 z-10 object-cover ${
          showPreview && "opacity-0"
        }`}
        loop
        onClick={handleVideoClick}
        onLoadedData={markVideoReady}
        onCanPlay={markVideoReady}
        onCanPlayThrough={markVideoReady}
        onWaiting={() => {
          if (wasPlayIconClicked) {
            setVideoLoader(true);
          }
        }}
        onPlaying={() => {
          setVideoLoader(false);
        }}
        onLoadedMetadata={() => setMetadataLoad(true)}
        autoPlay={true}
        muted
        playsInline
        preload="auto"
        src={
          videoSource &&
          (isActive && previewLoaded ? videoSource : "")
        }
        controls={false}
      >
        Your browser does not support the video tag.
      </video>

      {isActive && previewLoaded && showPlayIcon && (
        <div
          onClick={handleIconClick}
          className="absolute top-[48%] left-1/2 -translate-x-1/2 -translate-y-1/2 z-[40] transform-gpu duration-50 ease-in-out text-white cursor-pointer active:scale-95"
        >
          <PlayIcon />
        </div>
      )}
    </div>
  );
};

export default CustomVideoPlayer;
